import React, { useEffect, useState } from 'react';
import { maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import type { BreadcrumbLink } from '@noths/polaris-client-ribbons-design-system';
import { NotificationInfo, PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton/BackToTopButton';
import { BreadcrumbsContainer } from 'src/components/molecules/Breadcrumbs/BreadcrumbsContainer';
import { CopyBlockContainer } from 'src/components/molecules/CopyBlock/CopyBlockContainer';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { ProductListError } from 'src/components/molecules/ProductListError/ProductListError';
import { ActiveFacetsContainer } from 'src/components/organisms/ActiveFacets/ActiveFacetsContainer';
import { HeaderCopyBlockContainer } from 'src/components/organisms/HeaderCopyBlock/HeaderCopyBlockContainer';
import type { LinkCarouselContainerProps } from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import {
  LinkCarouselContainer,
  LinkCarouselTrackingCategory,
} from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import { ListingsControlsDrawerContainer } from 'src/components/organisms/ListingsControlsDrawer/ListingsControlsDrawerContainer';
import { ListingsControlsSideBar } from 'src/components/organisms/ListingsControlsSideBar/ListingsControlsSideBar';
import { ListingsHeaderContainer } from 'src/components/organisms/ListingsHeader/ListingsHeaderContainer';
import { RELATED_CATEGORIES_TITLE } from 'src/components/organisms/ProductPages/constants/copy';
import { ProductPagesContainer } from 'src/components/organisms/ProductPages/containers/ProductPagesContainer';
import * as elementIds from 'src/constants/elementIds';
import { useFilterNavType } from 'src/hooks/useFilterNavType/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { useStore } from 'src/redux/store';
import type { GetLDItemListSchemaArgs } from 'src/seo/linkedData/getLDItemListSchema';
import * as styles from 'src/styles/pages.styles';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';

const {
  serverRuntimeConfig: { CLEAR_PREVIEW_API_URL },
} = getConfig() as NextConfig;

export type CategoryListingProps = PageProps & {
  bottomLinks: LinkCarouselContainerProps['links'];
  breadcrumbLinks: BreadcrumbLink[];
  hideRecentlyViewedPlacement: boolean;
  linkedDataSchema?: GetLDItemListSchemaArgs;
  pageLiveRegionText: string;
  pageTitle: string;
  paginationTagValues: (string | null)[];
  preview: boolean;
  topLinks: LinkCarouselContainerProps['links'];
};

type CategoryProductsSectionProps = Pick<
  CategoryListingProps,
  'bottomLinks' | 'products' | 'showProductsList'
>;

const CategoryProductsSection = ({
  bottomLinks,
  products,
  showProductsList,
}: CategoryProductsSectionProps) => {
  if (products.error) {
    return <ProductListError message={products.error.message} />;
  }

  if (!showProductsList) {
    return <ProductListError message="No products found" />;
  }

  return (
    <div id={elementIds.PRODUCT_CONTAINER_WRAPPER}>
      <div css={[styles.grid, maxWidthContainer, styles.productListWrapper]}>
        <ActiveFacetsContainer />
        <ListingsHeaderContainer />
        <hr css={styles.productListSeparator} />
        <div css={styles.productListSidebar}>
          <ListingsControlsSideBar />
        </div>
        <div css={styles.productPagesWrapper}>
          <ProductPagesContainer />
          {bottomLinks.length > 0 && (
            <div css={styles.relatedCategories}>
              <LinkCarouselContainer
                links={bottomLinks}
                title={RELATED_CATEGORIES_TITLE}
                trackingCategory={LinkCarouselTrackingCategory.RelatedCategory}
              />
            </div>
          )}
          <CopyBlockContainer />
          <MembershipBannerSection trackingLocation="category" />
          <BackToTopButton />
          <ListingsControlsDrawerContainer />
        </div>
      </div>
    </div>
  );
};

export const CategoryListing = ({
  bottomLinks,
  breadcrumbLinks = [],
  pageLiveRegionText,
  preview,
  showProductsList,
  topLinks,
  updatedReduxState,
}: CategoryListingProps) => {
  const { query } = useRouter();
  const { dispatch } = useStore();
  const { products } = updatedReduxState as ReduxApplicationState;
  const [redirectNotification, setRedirectNotification] = useState(false);

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      {preview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.breadcrumbsContentWrapper}>
            <BreadcrumbsContainer breadcrumbsLinks={breadcrumbLinks} />
          </div>
        </div>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.headerContentWrapper}>
            {redirectNotification && (
              <NotificationInfo>
                We don&apos;t seem to have that product anymore, but here are some similar items
                below.
              </NotificationInfo>
            )}
            <HeaderCopyBlockContainer />
            {topLinks.length > 0 && (
              <section css={styles.linksCarouselSection}>
                <LinkCarouselContainer
                  links={topLinks}
                  trackingCategory={LinkCarouselTrackingCategory.ChildCategory}
                />
              </section>
            )}
          </div>
        </div>
        <CategoryProductsSection
          bottomLinks={bottomLinks}
          products={products}
          showProductsList={showProductsList}
        />
      </div>
    </>
  );
};
